import { useCallback, useEffect, useRef, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';
import { FileToGithubModal } from './FileToGithubModal';

interface Range {
    startLine: number;
    startCol: number;
    endLine: number;
    endCol: number;
}
interface Comment {
    id: string;
    text: string;
    anchor: { path: string; commitSha: string; quote?: string; range?: Range };
    authorEmail: string;
    authorDisplayName?: string;
    createdAt: string;
    status: 'open' | 'filed-to-github' | 'resolved' | 'deleted';
    githubIssueNumber?: number;
    githubIssueUrl?: string;
    issueCategory?: 'bug' | 'feature-request' | 'question';
    parentId?: string;
    drawingId?: string;
}

interface PendingDrawing {
    drawingId: string;
    /** Full `data:image/png;base64,…` URL for the in-app preview thumbnail. */
    pngBase64: string;
}

interface Attachment {
    filename: string;
    url: string;
    sizeBytes: number;
    mimeType?: string;
}

interface Props {
    repoId: string | null;
    path: string | null;
    commitSha: string | null;
    selectedQuote: string | null;
    selectedRange?: Range | null;
    onConsumeQuote: () => void;
    pendingAnnotation?: { drawingId: string; previewDataUrl: string } | null;
    onConsumeAnnotation?: () => void;
}

/** Read a File into a base64 string (no `data:` prefix). */
function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const idx = result.indexOf(',');
            resolve(idx === -1 ? result : result.slice(idx + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
        reader.readAsDataURL(file);
    });
}

export function CommentPanel({ repoId, path, commitSha, selectedQuote, selectedRange, onConsumeQuote, pendingAnnotation, onConsumeAnnotation }: Props) {
    const { userInfo, tokenReady } = useAuth();
    const [comments, setComments] = useState<Comment[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);
    const [text, setText] = useState('');
    const [quote, setQuote] = useState<string | null>(null);
    const [selectionRange, setSelectionRange] = useState<Range | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [fileTarget, setFileTarget] = useState<Comment | null>(null);
    const [pendingDrawing, setPendingDrawing] = useState<PendingDrawing | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        if (!repoId || !path) {
            setComments([]);
            return;
        }
        setLoading(true);
        try {
            const [owner, name] = repoId.split('/');
            const r = await apiCall<Comment[]>(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/comments?path=${encodeURIComponent(path)}`
            );
            setComments(r);
            setError(null);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setLoading(false);
        }
    }, [repoId, path]);

    useEffect(() => {
        if (tokenReady) void load();
    }, [tokenReady, load]);

    useEffect(() => {
        if (selectedQuote) {
            setQuote(selectedQuote);
            setSelectionRange(selectedRange ?? null);
            setComposerOpen(true);
            onConsumeQuote();
        }
    }, [selectedQuote, selectedRange, onConsumeQuote]);

    useEffect(() => {
        if (pendingAnnotation) {
            setPendingDrawing({
                drawingId: pendingAnnotation.drawingId,
                pngBase64: pendingAnnotation.previewDataUrl
            });
            setComposerOpen(true);
            onConsumeAnnotation?.();
        }
    }, [pendingAnnotation, onConsumeAnnotation]);

    async function uploadFile(file: File) {
        if (!repoId) return;
        if (file.size > 5 * 1024 * 1024) {
            setError(`${file.name} is larger than 5 MB. Pick a smaller file.`);
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const base64 = await fileToBase64(file);
            const [owner, name] = repoId.split('/');
            const r = await apiCall<Attachment>(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/attachments`,
                {
                    method: 'POST',
                    body: { filename: file.name, base64, mimeType: file.type }
                }
            );
            setAttachments((a) => [...a, r]);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    async function submit() {
        if (!repoId || !path || !commitSha) return;
        const t = text.trim();
        if (!t) return;
        setSubmitting(true);
        try {
            const [owner, name] = repoId.split('/');
            const submissionId = crypto.randomUUID();
            const created = await apiCall<Comment>(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/comments?path=${encodeURIComponent(path)}`,
                {
                    method: 'POST',
                    body: {
                        submissionId,
                        text: t,
                        anchor: {
                            commitSha,
                            quote: quote ?? undefined,
                            range: selectionRange ?? undefined
                        },
                        drawingId: pendingDrawing?.drawingId,
                        attachments: attachments.length > 0 ? attachments : undefined
                    }
                }
            );
            setText('');
            setQuote(null);
            setSelectionRange(null);
            setComposerOpen(false);
            setPendingDrawing(null);
            setAttachments([]);
            await load();
            // Default: open the file-to-GitHub modal right away. The user can
            // skip via the modal's "Save as comment only" action if they want
            // to keep it in-app for now and file later.
            if (created && created.status === 'open') {
                setFileTarget(created);
            }
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    async function del(comment: Comment) {
        if (!repoId || !path) return;
        const msg = comment.githubIssueNumber
            ? `Delete this comment? Linked GitHub issue #${comment.githubIssueNumber} will also be closed.`
            : 'Delete this comment?';
        if (!window.confirm(msg)) return;
        try {
            const [owner, name] = repoId.split('/');
            await apiCall(
                `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/comments/${comment.id}?path=${encodeURIComponent(path)}`,
                { method: 'DELETE' }
            );
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }

    if (!repoId || !path) {
        return <div className="comment-panel comment-panel--empty">Pick a file to see comments.</div>;
    }

    const visible = comments.filter((c) => c.status !== 'deleted');

    return (
        <div className="comment-panel">
            <header className="comment-panel__header">
                <h2>Comments</h2>
                <button
                    className="btn btn--ghost btn--small"
                    onClick={() => {
                        setComposerOpen((x) => !x);
                        setQuote(null);
                        setPendingDrawing(null);
                    }}
                >
                    {composerOpen ? 'Cancel' : '+ New'}
                </button>
            </header>

            {composerOpen && (
                <div className="comment-composer">
                    {quote && (
                        <div className="comment-composer__quote">
                            <span className="comment-composer__quote-label">Quoting:</span>
                            <blockquote>{quote}</blockquote>
                            <button className="btn btn--ghost btn--small" onClick={() => setQuote(null)}>
                                Drop quote
                            </button>
                        </div>
                    )}
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="What's the issue, suggestion, or question?"
                        rows={4}
                        autoFocus
                    />
                    {pendingDrawing && (
                        <div className="comment-composer__drawing">
                            <img
                                src={pendingDrawing.pngBase64}
                                alt="Attached annotation"
                                className="comment-composer__drawing-thumb"
                            />
                            <button
                                className="btn btn--ghost btn--small"
                                onClick={() => setPendingDrawing(null)}
                            >
                                Drop annotation
                            </button>
                        </div>
                    )}
                    {!pendingDrawing && (
                        <p className="form-hint">
                            Need to point at something? Use <strong>✎ Annotate</strong> in the doc toolbar to draw on
                            the page, then save — it attaches here.
                        </p>
                    )}
                    {attachments.length > 0 && (
                        <ul className="comment-composer__attachments">
                            {attachments.map((a) => (
                                <li key={a.url}>
                                    📎 <span className="comment-composer__att-name">{a.filename}</span>
                                    <span className="comment-composer__att-size">
                                        {a.sizeBytes >= 1024 * 1024
                                            ? `${(a.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                                            : `${Math.ceil(a.sizeBytes / 1024)} KB`}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn--ghost btn--small"
                                        onClick={() => setAttachments((arr) => arr.filter((x) => x.url !== a.url))}
                                        aria-label="Remove attachment"
                                    >
                                        ×
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="comment-composer__actions">
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void uploadFile(f);
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading || submitting}
                            title="Attach a file to this comment (max 5 MB)"
                        >
                            {uploading ? 'Uploading…' : '📎 Attach'}
                        </button>
                        <button className="btn btn--primary btn--small" onClick={submit} disabled={submitting || uploading || !text.trim()}>
                            {submitting ? 'Saving…' : 'Post'}
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="comment-panel__error">{error}</div>}
            {loading && visible.length === 0 && <div className="comment-panel__loading">Loading…</div>}
            {!loading && visible.length === 0 && !composerOpen && <div className="comment-panel__empty">No comments yet.</div>}

            <ul className="comment-list">
                {visible.map((c) => (
                    <li key={c.id} className={'comment' + (c.status === 'filed-to-github' ? ' comment--filed' : '')}>
                        <header className="comment__header">
                            <span className="comment__author">{c.authorDisplayName ?? c.authorEmail}</span>
                            <time className="comment__time" dateTime={c.createdAt}>
                                {new Date(c.createdAt).toLocaleString()}
                            </time>
                        </header>
                        {c.anchor.range && (
                            <div className="comment__range">
                                Lines {c.anchor.range.startLine}
                                {c.anchor.range.endLine !== c.anchor.range.startLine && `–${c.anchor.range.endLine}`}
                            </div>
                        )}
                        {c.anchor.quote && <blockquote className="comment__quote">{c.anchor.quote}</blockquote>}
                        <p className="comment__body">{c.text}</p>
                        {c.drawingId && <div className="comment__drawing-note">Includes drawing</div>}
                        {c.status === 'filed-to-github' && c.githubIssueNumber && (
                            <div className="comment__filed">
                                Filed as{' '}
                                <a href={c.githubIssueUrl} target="_blank" rel="noreferrer noopener">
                                    #{c.githubIssueNumber}
                                </a>{' '}
                                ({c.issueCategory})
                            </div>
                        )}
                        <div className="comment__actions">
                            {c.status === 'open' && c.authorEmail === userInfo?.email && (
                                <button className="btn btn--ghost btn--small" onClick={() => setFileTarget(c)}>
                                    Send to GitHub →
                                </button>
                            )}
                            {(c.authorEmail === userInfo?.email || userInfo?.role === 'admin') && (
                                <button className="btn btn--ghost btn--small" onClick={() => void del(c)}>
                                    Delete
                                </button>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            {fileTarget && (
                <FileToGithubModal
                    repoId={repoId}
                    comment={fileTarget}
                    onClose={() => setFileTarget(null)}
                    onFiled={() => {
                        setFileTarget(null);
                        void load();
                    }}
                />
            )}

        </div>
    );
}
